import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, ArrowUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { initiativesAPI } from '../lib/api';
import { convertGoogleDriveUrl } from '../lib/imageUtils';
import { prepareArticleContent } from '../lib/contentUtils';
import { CommentSection } from '../components/common/CommentSection';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1597132441547-a4fcd316da61?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function InitiativeDetail() {
  const { id } = useParams();
  const [initiative, setInitiative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInitiative = async () => {
      try {
        const response = await initiativesAPI.getOne(id);
        setInitiative(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Initiative not found' : 'Failed to load initiative');
      } finally {
        setLoading(false);
      }
    };
    fetchInitiative();
  }, [id]);

  // Handle smooth scrolling for citation links
  useEffect(() => {
    const handleCitationClick = (e) => {
      const target = e.target;
      if (target.classList.contains('citation-marker') || target.classList.contains('back-to-cite')) {
        e.preventDefault();
        const href = target.getAttribute('href');
        const targetElement = document.querySelector(href);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetElement.classList.add('citation-highlight');
          setTimeout(() => {
            targetElement.classList.remove('citation-highlight');
          }, 2000);
        }
      }
    };

    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', handleCitationClick);
  }, []);

  const statusColors = {
    ongoing: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    upcoming: 'bg-amber-100 text-amber-800'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !initiative) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <h1 className="font-heading text-2xl text-foreground mb-4">{error || 'Initiative not found'}</h1>
        <Link to="/initiatives">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Initiatives
          </Button>
        </Link>
      </div>
    );
  }

  const citations = initiative.citations || [];
  const processedContent = prepareArticleContent(initiative.content, citations);

  return (
    <div className="paper-texture min-h-screen" data-testid="initiative-detail-page">
      {/* Hero Image */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img 
          src={convertGoogleDriveUrl(initiative.featured_image) || PLACEHOLDER_IMAGE}
          alt={initiative.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="article-container">
            <Link to="/initiatives" className="inline-flex items-center text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Initiatives
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="article-container py-12 md:py-16">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="article-section"
        >
          <span className={`inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider ${statusColors[initiative.status] || statusColors.ongoing} mb-4 rounded`}>
            {initiative.status}
          </span>
          <h1 className="article-title text-3xl md:text-4xl lg:text-5xl mb-6">
            {initiative.title}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            {initiative.description}
          </p>
        </motion.header>

        <div className="gold-line mb-8"></div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="article-section-lg"
        >
          {processedContent && (
            <div 
              className="article-content"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />
          )}

          {/* References Section */}
          {citations.length > 0 && (
            <section className="references-section mt-12 pt-8 border-t border-border" id="references">
              <div className="flex items-center gap-2 mb-6">
                <BookOpen className="h-5 w-5 text-accent" />
                <h3 className="font-heading text-xl font-semibold text-foreground">
                  Sources & References
                </h3>
              </div>
              <ol className="references-list space-y-3">
                {citations.map((citation, idx) => (
                  <li 
                    key={idx} 
                    id={`ref-${idx + 1}`}
                    className="reference-item flex items-start gap-3 p-3 rounded-md transition-colors"
                  >
                    <span className="reference-number shrink-0 w-6 h-6 bg-accent/10 text-accent rounded text-xs font-semibold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div className="reference-content flex-1 min-w-0">
                      <span 
                        className="citation-text"
                        dangerouslySetInnerHTML={{ __html: citation }} 
                      />
                    </div>
                    <a 
                      href={`#cite-${idx + 1}`}
                      className="back-to-cite shrink-0 text-accent hover:text-accent/80 transition-colors"
                      title="Back to text"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </a>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </motion.div>
      </article>

      {/* Comments Section */}
      <div className="article-container py-8">
        <CommentSection 
          contentType="initiative" 
          contentId={id}
        />
      </div>

      {/* Back to Initiatives */}
      <section className="py-12 bg-muted/30 border-t border-border">
        <div className="article-container text-center">
          <Link to="/initiatives">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              View All Initiatives
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
